import { useEffect, useState } from 'react';
import { HFBox, HFButton, HFCard, HFChip, HFSkeleton, HFTooltip, useHfTheme } from '@hf/designSystem/v1';
import { omsRuleListSchema, type OmsCheck, type OmsRule, type OmsRuleList, type OmsRuleStage } from '@hf/sharedContract';
import { useHttp } from '@core/utils/http';

type StatusTone = 'pass' | 'warn' | 'block' | 'off';

const RESULT: Record<OmsCheck, { label: string; tone: StatusTone }> = {
  PASS: { label: 'Pass', tone: 'pass' },
  WARN: { label: 'Warn', tone: 'warn' },
  BLOCK: { label: 'Block', tone: 'block' },
  NOT_CHECKED: { label: 'Not checked', tone: 'off' },
};

const LABEL_SX = {
  m: 0,
  fontSize: 12,
  fontWeight: 700,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  color: 'text.secondary',
  lineHeight: '24px',
} as const;

function menuMark(rule: OmsRule): { label: string; tone: StatusTone; dot: StatusTone } {
  if (!rule.available || !rule.enabled) return { label: '', tone: 'off', dot: 'off' };
  if (rule.lastResult === 'BLOCK') return { label: 'Block', tone: 'block', dot: 'block' };
  if (rule.lastResult === 'WARN') return { label: 'Warn', tone: 'warn', dot: 'warn' };
  if (rule.lastResult === 'PASS') return { label: 'Pass', tone: 'pass', dot: 'pass' };
  return { label: '', tone: 'pass', dot: 'pass' };
}

function StatusChip({ tone, label }: { tone: StatusTone; label: string }) {
  const theme = useHfTheme();
  const palette = tone === 'pass' ? theme.palette.success : tone === 'warn' ? theme.palette.warning : tone === 'block' ? theme.palette.error : null;
  const fg = tone === 'pass' && theme.palette.mode === 'dark' ? theme.palette.background.default : palette?.contrastText;
  return (
    <HFChip
      tone={tone}
      label={label}
      sx={{
        height: 24,
        flexShrink: 0,
        fontWeight: 800,
        ...(palette
          ? { bgcolor: palette.main, color: fg, border: 0, '& .MuiChip-label': { color: 'inherit', px: 1, py: 0 } }
          : { color: 'text.primary' }),
      }}
    />
  );
}

function HitLabel({ tone, children }: { tone: StatusTone; children: string }) {
  const color = tone === 'pass' ? 'success.main' : tone === 'warn' ? 'warning.main' : tone === 'block' ? 'error.main' : 'text.secondary';
  return (
    <HFBox component="span" sx={{ flexShrink: 0, fontSize: 11, fontWeight: 800, letterSpacing: '0.04em', lineHeight: '16px', color }}>
      {children}
    </HFBox>
  );
}

function StatusDot({ tone }: { tone: StatusTone }) {
  const color = tone === 'pass' ? 'success.main' : tone === 'warn' ? 'warning.main' : tone === 'block' ? 'error.main' : 'text.secondary';
  return (
    <HFBox
      component="span"
      aria-hidden
      sx={{
        width: 10,
        height: 10,
        borderRadius: '50%',
        flexShrink: 0,
        bgcolor: color,
        boxShadow: (theme) => `0 0 0 3px ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'}`,
      }}
    />
  );
}

function groupsOf(rules: readonly OmsRule[]): Array<{ label: string; rules: OmsRule[] }> {
  const groups: Array<{ label: string; rules: OmsRule[] }> = [];
  for (const rule of rules) {
    const group = groups.find((item) => item.label === rule.group);
    if (group) group.rules.push(rule);
    else groups.push({ label: rule.group, rules: [rule] });
  }
  return groups;
}

function fieldsOf(rule: OmsRule): string {
  if (rule.source !== 'cloud' || !rule.logic) return 'HedgeFacts Applications returns no settings for this rule.';
  if (!rule.logic.against) return 'This check reads the order and the settings stored for it.';
  if (rule.logic.subjects === 'traded') return 'Compared fields are the symbol, the side and the books on orders that have already traded.';
  return 'Compared fields are the symbol, the side and the books on the Preview ticket and on each working order.';
}

function explanation(rule: OmsRule): string {
  if (rule.source === 'desktop') return 'Read-only. This check runs in HedgeFacts Applications. The Cloud does not evaluate it.';
  if (rule.source === 'cloud')
    return 'The Cloud evaluates this check from the live order read. A later editor can change the variables and the logic.';
  return 'Coming soon. The definition is stored. HedgeFacts Applications and the Cloud do not run this check yet.';
}

function Label({ children }: { children: string }) {
  return (
    <HFBox component="span" sx={LABEL_SX}>
      {children}
    </HFBox>
  );
}

/** Read-only PreTrade or Post Trade checks. Edit stays off. Cloud checks use the live order read. */
export function RuleBrowser({ stage, date }: { stage: OmsRuleStage; date?: string }) {
  const { request } = useHttp();
  const [rules, setRules] = useState<OmsRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setFailed(false);
    const path = date ? `/oms/rules?stage=${stage}&date=${encodeURIComponent(date)}` : `/oms/rules?stage=${stage}`;
    void request<OmsRuleList>(path, { responseSchema: omsRuleListSchema }).then((result) => {
      if (cancelled) return;
      if (result.status === 200 && result.data) {
        setRules(result.data.rules);
        setFailed(false);
      } else {
        setRules([]);
        setFailed(true);
      }
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [date, request, stage]);

  const post = stage === 'post';
  const menuLabel = post ? 'Post trade rule menu' : 'PreTrade rule menu';
  const intro = post
    ? 'Block is a hard breach. Warn needs a review. Coming soon means the check is in the catalogue and is not evaluated yet.'
    : 'Block stops a ticket. Warn needs an acknowledgement. Coming soon means the check is in the catalogue and is not evaluated yet.';
  const resultLabel = post ? 'Last run' : 'Last Preview result';
  const emptyResult = post ? 'This check has not been run yet.' : 'No Preview ticket has been checked against this rule yet.';
  const hitsLabel = post ? 'Breaches now' : 'Preview tickets hit now';
  const hitsEmpty = post ? 'No breach is open for this check.' : 'No ticket in Order Preview hits this rule.';
  const appliedLabel = post ? 'Limit' : 'Applied at';
  const selected = rules.find((rule) => rule.ruleId === selectedId) ?? rules.find((rule) => rule.available && rule.enabled) ?? rules[0];
  const active = rules.filter((rule) => rule.available && rule.enabled).length;
  const off = rules.filter((rule) => rule.available && !rule.enabled).length;
  const comingSoon = rules.filter((rule) => !rule.available).length;
  const result = selected?.lastResult ? RESULT[selected.lastResult] : null;

  const live = Boolean(selected?.available && selected.enabled);

  const columns = { xs: '1fr', lg: '280px minmax(0, 1fr) 300px' } as const;

  return (
    <HFBox aria-busy={loading} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {loading ? (
        <HFBox sx={{ display: 'grid', gridTemplateColumns: columns, gap: 2 }}>
          <HFCard component="nav" aria-label={menuLabel}>
            <HFSkeleton data-testid="rules-skeleton" variant="rounded" height={420} />
          </HFCard>
          <HFSkeleton variant="rounded" height={420} />
          <HFSkeleton variant="rounded" height={280} sx={{ display: { xs: 'none', lg: 'block' } }} />
        </HFBox>
      ) : (
        <>
          <HFBox sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 3 }}>
            <HFBox component="p" sx={{ m: 0, flex: '1 1 420px', fontSize: 14, lineHeight: 1.5, color: 'text.secondary' }}>
              {intro}
            </HFBox>
            <HFBox sx={{ display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 }}>
              {failed ? null : (
                <HFBox component="p" sx={{ m: 0, fontSize: 13, fontWeight: 700, whiteSpace: 'nowrap' }}>
                  {[`${active} of ${rules.length} rules active`, off > 0 ? `${off} off` : null, `${comingSoon} coming soon`]
                    .filter(Boolean)
                    .join(' · ')}
                </HFBox>
              )}
              <HFTooltip title="Editing rules comes in a later phase.">
                <HFBox component="span" sx={{ display: 'inline-flex' }}>
                  <HFButton size="small" variant="outlined" disabled>
                    Edit rule
                  </HFButton>
                </HFBox>
              </HFTooltip>
            </HFBox>
          </HFBox>
          <HFBox sx={{ display: 'grid', gridTemplateColumns: columns, gap: 2, alignItems: 'start' }}>
            <HFCard
              component="nav"
              aria-label={menuLabel}
              sx={{ position: { md: 'sticky' }, top: 80, maxHeight: { md: 'calc(100dvh - 104px)' }, height: 'auto', overflow: 'auto' }}
            >
              {failed ? (
                <HFBox sx={{ color: 'text.secondary', fontSize: 14 }}>Rules unavailable.</HFBox>
              ) : (
                <HFBox sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Label>{menuLabel}</Label>
                  {groupsOf(rules).map((group) => (
                    <HFBox key={group.label} sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <Label>{group.label}</Label>
                      {group.rules.map((rule) => {
                        const pressed = rule.ruleId === selected?.ruleId;
                        const mark = menuMark(rule);
                        return (
                          <HFButton
                            key={rule.ruleId}
                            variant="text"
                            aria-pressed={pressed}
                            onClick={() => setSelectedId(rule.ruleId)}
                            sx={{
                              justifyContent: 'flex-start',
                              alignItems: 'center',
                              width: '100%',
                              height: 'auto',
                              minHeight: 40,
                              px: 1,
                              py: 1,
                              whiteSpace: 'normal',
                              textTransform: 'none',
                              textAlign: 'left',
                              fontWeight: 600,
                              fontSize: 13,
                              lineHeight: 1.35,
                              borderRadius: 1,
                              color: 'text.primary',
                              bgcolor: pressed ? 'action.selected' : 'transparent',
                              borderLeft: '3px solid',
                              borderColor: pressed ? 'primary.main' : 'transparent',
                            }}
                          >
                            <HFBox component="span" sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', minWidth: 0 }}>
                              <StatusDot tone={mark.dot} />
                              <HFBox component="span" sx={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
                                {rule.name}
                              </HFBox>
                              {mark.label ? <HitLabel tone={mark.tone}>{mark.label}</HitLabel> : null}
                            </HFBox>
                          </HFButton>
                        );
                      })}
                    </HFBox>
                  ))}
                </HFBox>
              )}
            </HFCard>

            <HFCard component="section" aria-label="Selected rule" sx={{ height: 'auto' }}>
              <HFBox sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <HFBox sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', minHeight: 40 }}>
                  {selected ? (
                    <HFBox component="h2" sx={{ m: 0, fontSize: 20, fontWeight: 800, letterSpacing: '-0.02em', lineHeight: '24px' }}>
                      {selected.name}
                    </HFBox>
                  ) : null}
                  {selected?.effect === 'block' || selected?.effect === 'block-or-warn' ? <StatusChip tone="block" label="Block" /> : null}
                  {selected?.effect === 'warn' || selected?.effect === 'block-or-warn' ? <StatusChip tone="warn" label="Warn" /> : null}
                  {selected ? (
                    <StatusChip tone={live ? 'pass' : 'off'} label={!selected.available ? 'Coming soon' : selected.enabled ? 'Active' : 'Off'} />
                  ) : null}
                </HFBox>
                {selected ? (
                  <HFBox component="p" sx={{ m: 0, fontSize: 13, color: 'text.secondary', lineHeight: 1.5 }}>
                    {explanation(selected)}
                  </HFBox>
                ) : null}

                {selected ? (
                  <>
                    <HFBox component="p" sx={{ m: 0, fontSize: 15, lineHeight: 1.55 }}>
                      {selected.description}
                    </HFBox>
                    <HFBox sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, borderRadius: 1, bgcolor: 'action.hover' }}>
                      <Label>{resultLabel}</Label>
                      <StatusChip tone={result?.tone ?? 'off'} label={result?.label ?? 'No result yet'} />
                      <HFBox component="p" sx={{ m: 0, flex: 1, fontSize: 14, lineHeight: '24px', color: 'text.secondary' }}>
                        {selected.lastResult == null ? emptyResult : null}
                      </HFBox>
                    </HFBox>
                    {(
                      [
                        [appliedLabel, selected.appliedTo ?? 'Not set up in HedgeFacts Applications.'],
                        ['Rule fields', fieldsOf(selected)],
                        ['Compliance notification', 'Compliance e-mails arrive with rule settings in a later phase.'],
                      ] as const
                    ).map(([label, value]) => (
                      <HFBox key={label} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Label>{label}</Label>
                        <HFBox component="p" sx={{ m: 0, fontSize: 14, lineHeight: 1.5 }}>
                          {value}
                        </HFBox>
                      </HFBox>
                    ))}
                  </>
                ) : null}
              </HFBox>
            </HFCard>

            <HFBox sx={{ display: 'flex', flexDirection: 'column', gap: 2, position: { lg: 'sticky' }, top: 80 }}>
              <HFCard component="section" aria-label={hitsLabel}>
                <HFBox sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Label>{hitsLabel}</Label>
                  {(selected?.hitOrderIds ?? []).length > 0 ? (
                    (selected?.hitOrderIds ?? []).map((id) => (
                      <HFBox key={id} component="p" sx={{ m: 0, fontSize: 14, lineHeight: 1.5 }}>
                        {`Order ${id} hits`}
                      </HFBox>
                    ))
                  ) : (
                    <HFBox component="p" sx={{ m: 0, fontSize: 14, lineHeight: 1.5, color: 'text.secondary' }}>
                      {hitsEmpty}
                    </HFBox>
                  )}
                </HFBox>
              </HFCard>
              <HFCard component="section" aria-label="Change history">
                <HFBox sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <HFBox sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, minHeight: 24 }}>
                    <Label>Change history</Label>
                    <HFTooltip title="Export arrives in a later phase.">
                      <HFBox component="span" sx={{ display: 'inline-flex' }}>
                        <HFButton size="small" variant="outlined" disabled>
                          Export
                        </HFButton>
                      </HFBox>
                    </HFTooltip>
                  </HFBox>
                  <HFBox component="p" sx={{ m: 0, fontFamily: 'ui-monospace, monospace', fontSize: 12, lineHeight: 1.6, color: 'text.secondary' }}>
                    [date, time] [user]
                    <br />
                    [field]: [before] changed to [after]
                    <br />
                    Reason: [reason entered by the user]
                  </HFBox>
                  <HFBox component="p" sx={{ m: 0, fontSize: 14, lineHeight: 1.5, color: 'text.secondary' }}>
                    Every edit, waiver and on/off switch will list who, when and before / after here once rules can be edited.
                  </HFBox>
                </HFBox>
              </HFCard>
              <HFBox
                sx={{
                  border: 1,
                  borderStyle: 'dashed',
                  borderColor: 'divider',
                  borderRadius: 1,
                  p: 2,
                  fontSize: 13,
                  lineHeight: 1.5,
                  color: 'text.secondary',
                }}
              >
                Read-only. Desktop checks run in HedgeFacts Applications. Cloud checks use the live order read. Coming soon stays in the catalogue
                until a check is turned on.
              </HFBox>
            </HFBox>
          </HFBox>
        </>
      )}
    </HFBox>
  );
}
